import React, { useState, useEffect, useCallback } from 'react';
import { listServiceTypes } from '../services/serviceTypeService';
import { FaTimes, FaCheckSquare, FaRegSquare } from 'react-icons/fa';

const GST_RATE = 0.1; // 10% — hardcoded until configurable via global settings

/**
 * Modal for editing per-service-type pricing on a supplier (client).
 *
 * Props:
 *   clientName   {string}   Supplier display name
 *   initialPricing {object} Current servicePricing map from client doc
 *   onSave       {fn}       Called with new pricing map: { [serviceTypeId]: { name, pricePerHour, additionalPrice, invoicePricePerHour } }
 *   onClose      {fn}       Called when modal closes without saving
 */
const ServicePricingModal = ({ clientName, initialPricing = {}, onSave, onClose }) => {
    const [serviceTypes, setServiceTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Local pricing state: { [serviceTypeId]: { pricePerHour: '', additionalPrice: '' } }
    const [pricing, setPricing] = useState({});

    // Multi-select state
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Quick-fill bar values
    const [quickFse, setQuickFse] = useState('');
    const [quickInvoice, setQuickInvoice] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const types = await listServiceTypes();
                setServiceTypes(types);

                // Initialise pricing state from existing client data
                const init = {};
                types.forEach(t => {
                    const existing = initialPricing[t.id];
                    init[t.id] = {
                        pricePerHour: existing?.pricePerHour !== undefined ? String(existing.pricePerHour) : '',
                        additionalPrice: existing?.additionalPrice !== undefined ? String(existing.additionalPrice) : '',
                    };
                });
                setPricing(init);
            } catch (err) {
                setError('Failed to load service types.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSelectAll = () => {
        if (selectedIds.size === serviceTypes.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(serviceTypes.map(t => t.id)));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const applyQuickFill = () => {
        if (!quickFse && !quickInvoice) return;
        const targets = selectedIds.size > 0 ? selectedIds : new Set(serviceTypes.map(t => t.id));
        setPricing(prev => {
            const next = { ...prev };
            targets.forEach(id => {
                next[id] = {
                    pricePerHour: quickFse !== '' ? quickFse : prev[id]?.pricePerHour ?? '',
                    additionalPrice: quickInvoice !== '' ? quickInvoice : prev[id]?.additionalPrice ?? '',
                };
            });
            return next;
        });
    };

    const updateField = (id, field, value) => {
        setPricing(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value }
        }));
    };

    const computeGst = (additionalPrice) => {
        const val = parseFloat(additionalPrice);
        if (isNaN(val) || val <= 0) return '—';
        return `$${(Math.round(val * (1 + GST_RATE) * 100) / 100).toFixed(2)}`;
    };

    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);

        const newPricing = {};
        for (const t of serviceTypes) {
            const row = pricing[t.id] || {};
            const fse = parseFloat(row.pricePerHour);
            const inv = parseFloat(row.additionalPrice);
            // Only include service types where at least one price is set
            if (!isNaN(fse) || !isNaN(inv)) {
                newPricing[t.id] = {
                    name: t.name,
                    pricePerHour: !isNaN(fse) ? fse : null,
                    additionalPrice: !isNaN(inv) ? inv : null,
                    invoicePricePerHour: !isNaN(inv) ? Math.round(inv * (1 + GST_RATE) * 100) / 100 : null,
                };
            }
        }

        try {
            await onSave(newPricing);
        } catch (err) {
            setError(err.message || 'Failed to save pricing.');
            setSaving(false);
        }
    }, [serviceTypes, pricing, onSave]);

    const allSelected = serviceTypes.length > 0 && selectedIds.size === serviceTypes.length;
    const someSelected = selectedIds.size > 0 && !allSelected;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Edit Service Pricing</h2>
                        <p className="text-sm text-gray-500 mt-0.5">{clientName}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <FaTimes size={18} />
                    </button>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center py-16 text-gray-500">
                        Loading service types...
                    </div>
                ) : (
                    <>
                        {/* Quick Fill Bar */}
                        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
                            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                                Quick Fill {selectedIds.size > 0 ? `(${selectedIds.size} selected)` : '(all rows)'}
                            </p>
                            <div className="flex flex-wrap gap-3 items-end">
                                <div>
                                    <label className="block text-xs text-gray-600 mb-1">FSE Rate ($/hr)</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={quickFse}
                                        onChange={e => setQuickFse(e.target.value)}
                                        className="w-32 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        placeholder="e.g. 80"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-600 mb-1">Invoice ex. GST ($/hr)</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={quickInvoice}
                                        onChange={e => setQuickInvoice(e.target.value)}
                                        className="w-36 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        placeholder="e.g. 82"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={applyQuickFill}
                                    className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                                >
                                    Apply to {selectedIds.size > 0 ? 'Selected' : 'All'}
                                </button>
                            </div>
                        </div>

                        {/* Service Type Table */}
                        <div className="flex-1 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <th className="px-4 py-3 w-8">
                                            <button type="button" onClick={toggleSelectAll} className="text-gray-400 hover:text-gray-600">
                                                {allSelected ? <FaCheckSquare size={16} className="text-blue-500" /> :
                                                    someSelected ? <FaCheckSquare size={16} className="text-blue-300" /> :
                                                        <FaRegSquare size={16} />}
                                            </button>
                                        </th>
                                        <th className="px-4 py-3">Service Type</th>
                                        <th className="px-4 py-3 w-36">FSE Rate ($/hr)</th>
                                        <th className="px-4 py-3 w-40">Invoice ex. GST ($/hr)</th>
                                        <th className="px-4 py-3 w-36 text-right">Invoice w/ GST</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {serviceTypes.map(t => {
                                        const row = pricing[t.id] || {};
                                        const isSelected = selectedIds.has(t.id);
                                        return (
                                            <tr key={t.id} className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                                                <td className="px-4 py-2.5">
                                                    <button type="button" onClick={() => toggleSelect(t.id)} className="text-gray-400 hover:text-gray-600">
                                                        {isSelected
                                                            ? <FaCheckSquare size={16} className="text-blue-500" />
                                                            : <FaRegSquare size={16} />}
                                                    </button>
                                                </td>
                                                <td className="px-4 py-2.5 font-medium text-gray-800">{t.name}</td>
                                                <td className="px-4 py-2.5">
                                                    <div className="relative">
                                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                                                        <input
                                                            type="number" step="0.01" min="0"
                                                            value={row.pricePerHour || ''}
                                                            onChange={e => updateField(t.id, 'pricePerHour', e.target.value)}
                                                            className="w-full pl-5 pr-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jims-cyan"
                                                            placeholder="—"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2.5">
                                                    <div className="relative">
                                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                                                        <input
                                                            type="number" step="0.01" min="0"
                                                            value={row.additionalPrice || ''}
                                                            onChange={e => updateField(t.id, 'additionalPrice', e.target.value)}
                                                            className="w-full pl-5 pr-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-jims-cyan"
                                                            placeholder="—"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2.5 text-right font-medium text-gray-700">
                                                    {computeGst(row.additionalPrice)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        {error && (
                            <div className="px-6 py-2 bg-red-50 border-t border-red-100">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                            <p className="text-xs text-gray-400">Invoice w/ GST is auto-calculated at 10% GST</p>
                            <div className="flex gap-3">
                                <button type="button" onClick={onClose}
                                    className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button type="button" onClick={handleSave} disabled={saving}
                                    className="px-5 py-2 bg-jims-cyan text-white rounded text-sm font-medium hover:bg-jims-cyan-dark transition-colors disabled:opacity-50">
                                    {saving ? 'Saving…' : 'Save Pricing'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ServicePricingModal;
